import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import { Eye, X, Maximize2, Minimize2, Play, MapPin, ChevronLeft, ChevronRight, Loader2, Wifi, WifiOff, RotateCcw, ZoomIn, ZoomOut, Signal, Video, Hand, ArrowLeftRight } from 'lucide-react';
import {
  gaTrackMatterportTourStart,
  gaTrackMatterportSwipeGesture,
  gaTrackMatterportPinchZoom,
  gaTrackMatterportFullscreenExit,
  gaTrackMatterportQualitySwitch,
  gaTrackMatterportTourLoaded,
} from '../../../services/gaAnalyticsService';

const CATEGORY_LABELS = {
  infrastructure: 'Infrastructure',
  education: 'Éducation',
  sante: 'Santé',
  commerce: 'Commerce',
  loisirs: 'Loisirs & Culture',
  transport: 'Transport',
  residentiel: 'Résidentiel',
  autre: 'Autre',
};

const CATEGORY_COLORS = {
  infrastructure: 'bg-blue-100 text-blue-700',
  education: 'bg-purple-100 text-purple-700',
  sante: 'bg-red-100 text-red-700',
  commerce: 'bg-amber-100 text-amber-700',
  loisirs: 'bg-green-100 text-green-700',
  transport: 'bg-slate-100 text-slate-700',
  residentiel: 'bg-orange-100 text-orange-700',
  autre: 'bg-gray-100 text-gray-600',
};

// Detect connection quality for Abidjan mobile networks
const getConnectionQuality = () => {
  const conn = navigator?.connection || navigator?.mozConnection || navigator?.webkitConnection;
  if (!conn) return 'medium';
  const { effectiveType, downlink, saveData } = conn;
  if (saveData) return 'low';
  if (effectiveType === '4g' && downlink >= 5) return 'high';
  if (effectiveType === '4g' || (effectiveType === '3g' && downlink >= 1.5)) return 'medium';
  return 'low';
};

const QUALITY_PARAMS = {
  high:   { qs: '1', vr: '1', play: '1', hl: '1', title: '0', brand: '0' },
  medium: { qs: '0', vr: '0', play: '1', hl: '0', title: '0', brand: '0' },
  low:    { qs: '0', vr: '0', play: '1', hl: '0', title: '0', brand: '0', lp: '1' },
};

const QUALITY_LABELS = {
  high:   { label: 'HD', color: 'text-green-400', icon: Signal },
  medium: { label: 'SD', color: 'text-amber-400', icon: Wifi },
  low:    { label: 'Éco', color: 'text-red-400', icon: WifiOff },
};

// Estimated load times per quality (seconds) for Abidjan networks
const QUALITY_LOAD_ESTIMATES = {
  high:   { min: 3,  max: 8,  label: '~5 sec' },
  medium: { min: 6,  max: 18, label: '~12 sec' },
  low:    { min: 12, max: 35, label: '~20 sec' },
};

// Haptic patterns (milliseconds)
const HAPTIC_PATTERNS = {
  select:     [30],
  swipe:      [20, 30, 20],
  fullscreen: [40, 20, 40],
  close:      [60],
  zoom:       [15],
  loaded:     [20, 10, 20, 10, 40],
};

// Haptic feedback simulation via navigator.vibrate
const triggerHaptic = (pattern = 'select') => {
  try {
    if (navigator?.vibrate) {
      navigator.vibrate(HAPTIC_PATTERNS[pattern] || HAPTIC_PATTERNS.select);
    }
  } catch {
    // Silently ignore — not all browsers support vibrate
  }
};

// localStorage key to track first-time gesture hint
const GESTURE_HINT_KEY = 'kapital_matterport_hint_shown';

const getMatterportEmbedUrl = (url, quality = 'medium') => {
  if (!url) return null;
  try {
    const urlObj = new URL(url);
    const modelId =
      urlObj?.searchParams?.get('m') ||
      urlObj?.pathname?.split('/')?.filter(Boolean)?.pop();
    if (modelId && modelId?.length > 3) {
      const params = QUALITY_PARAMS?.[quality] || QUALITY_PARAMS?.medium;
      const qs = new URLSearchParams({ m: modelId, ...params });
      return `https://my.matterport.com/show/?${qs?.toString()}`;
    }
    return url;
  } catch {
    return url;
  }
};

// ── Touch Gesture Hook ──────────────────────────────────────────────────────
const useTouchGestures = ({ onSwipeLeft, onSwipeRight, onPinchZoom, enabled }) => {
  const ref = useRef(null);
  const touchState = useRef({
    startX: 0, startY: 0,
    lastDist: 0, isPinching: false,
    startTime: 0,
  });

  useEffect(() => {
    if (!enabled) return;
    const el = ref?.current;
    if (!el) return;

    const getDistance = (t1, t2) => {
      const dx = t1?.clientX - t2?.clientX;
      const dy = t1?.clientY - t2?.clientY;
      return Math.sqrt(dx * dx + dy * dy);
    };

    const onTouchStart = (e) => {
      if (e?.touches?.length === 2) {
        touchState.current.isPinching = true;
        touchState.current.lastDist = getDistance(e?.touches?.[0], e?.touches?.[1]);
      } else if (e?.touches?.length === 1) {
        touchState.current.isPinching = false;
        touchState.current.startX = e?.touches?.[0]?.clientX;
        touchState.current.startY = e?.touches?.[0]?.clientY;
        touchState.current.startTime = Date.now();
      }
    };

    const onTouchMove = (e) => {
      if (e?.touches?.length === 2 && touchState?.current?.isPinching) {
        const dist = getDistance(e?.touches?.[0], e?.touches?.[1]);
        const delta = dist - touchState?.current?.lastDist;
        touchState.current.lastDist = dist;
        if (Math.abs(delta) > 2) {
          onPinchZoom?.(delta > 0 ? 'in' : 'out', Math.abs(delta));
        }
        e?.preventDefault();
      }
    };

    const onTouchEnd = (e) => {
      if (touchState?.current?.isPinching) {
        touchState.current.isPinching = false;
        return;
      }
      if (e?.changedTouches?.length === 1) {
        const dx = e?.changedTouches?.[0]?.clientX - touchState?.current?.startX;
        const dy = e?.changedTouches?.[0]?.clientY - touchState?.current?.startY;
        const dt = Date.now() - touchState?.current?.startTime;
        if (dt < 400 && Math.abs(dx) > 60 && Math.abs(dy) < 80) {
          if (dx < 0) onSwipeLeft?.();
          else onSwipeRight?.();
        }
      }
    };

    el?.addEventListener('touchstart', onTouchStart, { passive: true });
    el?.addEventListener('touchmove', onTouchMove, { passive: false });
    el?.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      el?.removeEventListener('touchstart', onTouchStart);
      el?.removeEventListener('touchmove', onTouchMove);
      el?.removeEventListener('touchend', onTouchEnd);
    };
  }, [enabled, onSwipeLeft, onSwipeRight, onPinchZoom]);

  return ref;
};

// ── Enhanced Loading Overlay with Progress Bar ──────────────────────────────
const IframeLoadingOverlay = ({ visible, quality, tourTitle }) => {
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState('connecting');
  const progressRef = useRef(null);
  const estimate = QUALITY_LOAD_ESTIMATES?.[quality] || QUALITY_LOAD_ESTIMATES.medium;

  useEffect(() => {
    if (!visible) {
      setProgress(0);
      setPhase('connecting');
      if (progressRef.current) clearInterval(progressRef.current);
      return;
    }

    setProgress(0);
    setPhase('connecting');

    // Simulate realistic progress based on connection quality
    const totalMs = ((estimate.min + estimate.max) / 2) * 1000;
    const intervalMs = 120;
    const steps = totalMs / intervalMs;
    let current = 0;

    progressRef.current = setInterval(() => {
      current += 1;
      const pct = current / steps;
      // Ease-out curve: fast start, slow near end (never reaches 100 until actual load)
      const eased = Math.min(92, Math.round(100 * (1 - Math.pow(1 - pct, 2.5))));
      setProgress(eased);

      if (eased < 30) setPhase('connecting');
      else if (eased < 70) setPhase('loading');
      else setPhase('rendering');

      if (eased >= 92) clearInterval(progressRef.current);
    }, intervalMs);

    return () => { if (progressRef.current) clearInterval(progressRef.current); };
  }, [visible, quality]);

  const phaseLabels = {
    connecting: 'Connexion au serveur...',
    loading:    'Chargement de la visite 3D...',
    rendering:  'Rendu de la scène...',
  };

  const qualityInfo = QUALITY_LABELS?.[quality] || QUALITY_LABELS.medium;
  const QualityIcon = qualityInfo?.icon;

  if (!visible) return null;

  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-950 z-10">
      {/* Animated icon */}
      <div className="relative mb-6">
        <div className="w-16 h-16 rounded-2xl bg-brand-gold/10 border border-brand-gold/30 flex items-center justify-center">
          <Eye className="w-8 h-8 text-brand-gold animate-pulse" />
        </div>
        <div
          className="absolute -inset-1 rounded-2xl border border-brand-gold/20 animate-ping"
          style={{ animationDuration: '2s' }}
        />
      </div>

      {/* Tour title */}
      {tourTitle && (
        <p className="text-white font-semibold text-sm mb-1 text-center px-4 max-w-xs truncate">
          {tourTitle}
        </p>
      )}

      {/* Phase label */}
      <p className="text-gray-400 text-xs mb-4">{phaseLabels[phase]}</p>

      {/* Progress bar */}
      <div className="w-56 h-1.5 bg-gray-800 rounded-full overflow-hidden mb-2">
        <div
          className="h-full bg-gradient-to-r from-brand-gold/70 to-brand-gold rounded-full transition-all duration-200 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Progress % */}
      <p className="text-gray-500 text-xs mb-4 font-mono">{progress}%</p>

      {/* Network info */}
      <div className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-gray-900 border border-gray-800 ${qualityInfo?.color}`}>
        <QualityIcon className="w-3 h-3" />
        <span className="font-semibold">{qualityInfo?.label}</span>
        <span className="text-gray-500 ml-1">· Réseau Abidjan · {estimate.label}</span>
      </div>
    </div>
  );
};

// ── Gesture Hint Overlay (first-launch, persistent until dismissed) ──────────
const GestureHint = ({ visible, onDismiss, hasTours }) => {
  const [animStep, setAnimStep] = useState(0);

  useEffect(() => {
    if (!visible) return;
    const timer = setInterval(() => {
      setAnimStep(s => (s + 1) % 3);
    }, 1800);
    return () => clearInterval(timer);
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      className="absolute inset-0 z-20 flex items-end justify-center pb-8 bg-black/70 backdrop-blur-sm"
      onClick={onDismiss}
    >
      <div
        className="bg-gray-900/95 border border-white/10 rounded-2xl px-5 py-5 text-center max-w-xs mx-4 shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <p className="text-white font-semibold text-sm mb-4">Gestes de navigation</p>

        <div className="flex justify-center gap-5 mb-4">
          {/* Drag to rotate */}
          <div className={`flex flex-col items-center gap-1.5 transition-all duration-500 ${animStep === 0 ? 'opacity-100 scale-110' : 'opacity-50 scale-100'}`}>
            <div className="w-11 h-11 rounded-full border-2 border-brand-gold/60 flex items-center justify-center bg-brand-gold/10">
              <RotateCcw className="w-5 h-5 text-brand-gold" />
            </div>
            <span className="text-gray-300 text-xs font-medium">Glisser</span>
            <span className="text-gray-500 text-xs">Rotation 360°</span>
          </div>

          {/* Pinch to zoom */}
          <div className={`flex flex-col items-center gap-1.5 transition-all duration-500 ${animStep === 1 ? 'opacity-100 scale-110' : 'opacity-50 scale-100'}`}>
            <div className="w-11 h-11 rounded-full border-2 border-brand-gold/60 flex items-center justify-center bg-brand-gold/10">
              <ZoomIn className="w-5 h-5 text-brand-gold" />
            </div>
            <span className="text-gray-300 text-xs font-medium">Pincer</span>
            <span className="text-gray-500 text-xs">Zoom avant/arrière</span>
          </div>

          {/* Swipe to navigate */}
          {hasTours && (
            <div className={`flex flex-col items-center gap-1.5 transition-all duration-500 ${animStep === 2 ? 'opacity-100 scale-110' : 'opacity-50 scale-100'}`}>
              <div className="w-11 h-11 rounded-full border-2 border-brand-gold/60 flex items-center justify-center bg-brand-gold/10">
                <ArrowLeftRight className="w-5 h-5 text-brand-gold" />
              </div>
              <span className="text-gray-300 text-xs font-medium">Swipe</span>
              <span className="text-gray-500 text-xs">Visite suivante</span>
            </div>
          )}
        </div>

        {/* Animated swipe arrow */}
        <div className="flex items-center justify-center gap-1 mb-4 overflow-hidden h-5">
          <div
            className="flex items-center gap-1 text-brand-gold/60"
            style={{ animation: 'slideHint 1.8s ease-in-out infinite' }}
          >
            <ChevronLeft className="w-4 h-4" />
            <div className="w-8 h-0.5 bg-brand-gold/40 rounded-full" />
            <Hand className="w-4 h-4" />
            <div className="w-8 h-0.5 bg-brand-gold/40 rounded-full" />
            <ChevronRight className="w-4 h-4" />
          </div>
        </div>

        <button
          onClick={onDismiss}
          className="w-full py-2.5 bg-brand-gold text-white text-sm font-semibold rounded-xl active:scale-95 transition-transform touch-manipulation"
        >
          Commencer la visite
        </button>
        <p className="text-gray-600 text-xs mt-2">Touchez n'importe où pour fermer</p>
      </div>

      <style>{`
        @keyframes slideHint {
          0%, 100% { transform: translateX(0); opacity: 0.6; }
          50% { transform: translateX(6px); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

// ── Zoom Indicator ──────────────────────────────────────────────────────────
const ZoomIndicator = ({ direction, visible }) => {
  if (!visible) return null;
  return (
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-30 pointer-events-none">
      <div className="bg-black/70 border border-white/20 rounded-full p-3 animate-ping-once">
        {direction === 'in'
          ? <ZoomIn className="w-8 h-8 text-white" />
          : <ZoomOut className="w-8 h-8 text-white" />
        }
      </div>
    </div>
  );
};

// ── Persistent Swipe Hint Strip (dismissible) ───────────────────────────────
const SwipeHintStrip = ({ visible, onDismiss }) => {
  if (!visible) return null;
  return (
    <div className="bg-gray-900/90 px-4 py-2.5 flex items-center justify-between gap-2 text-gray-300 text-xs border-t border-white/5">
      <div className="flex items-center gap-2">
        <ArrowLeftRight className="w-3.5 h-3.5 text-brand-gold flex-shrink-0" />
        <span>Glissez gauche/droite pour changer de visite</span>
      </div>
      <button
        onClick={onDismiss}
        className="text-gray-500 hover:text-gray-300 transition-colors touch-manipulation p-1 -mr-1 flex-shrink-0"
        aria-label="Fermer l'indice"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

// ── Main Component ──────────────────────────────────────────────────────────
const ImmersiveTours = ({ commune, autoStartTrigger }) => {
  const [tours, setTours] = useState([]);
  const [activeTourIndex, setActiveTourIndex] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [iframeLoading, setIframeLoading] = useState(false);
  const [connectionQuality, setConnectionQuality] = useState('medium');
  const [showGestureHint, setShowGestureHint] = useState(false);
  const [showSwipeStrip, setShowSwipeStrip] = useState(false);
  const [zoomFeedback, setZoomFeedback] = useState({ visible: false, direction: 'in' });
  const [showControls, setShowControls] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const controlsTimerRef = useRef(null);
  const gestureHintShownRef = useRef(false);
  const prevTriggerRef = useRef(0);
  const pendingAutoStartRef = useRef(false);
  const fullscreenEnteredAtRef = useRef(null);
  const tourStartedAtRef = useRef(null);

  // Check if gesture hint was already shown (persistent across sessions via localStorage)
  const isFirstTourLaunch = useCallback(() => {
    try {
      return !localStorage.getItem(GESTURE_HINT_KEY);
    } catch {
      return !gestureHintShownRef.current;
    }
  }, []);

  const markGestureHintShown = useCallback(() => {
    gestureHintShownRef.current = true;
    try {
      localStorage.setItem(GESTURE_HINT_KEY, '1');
    } catch {
      // localStorage not available
    }
  }, []);

  // Show gesture hint on first launch, swipe strip on repeat visits with multiple tours
  const showGestureHintIfFirst = useCallback((hasManyTours) => {
    if (isMobile && isFirstTourLaunch()) {
      setShowGestureHint(true);
      markGestureHintShown();
    } else if (isMobile && hasManyTours) {
      setShowSwipeStrip(true);
    }
  }, [isMobile, isFirstTourLaunch, markGestureHintShown]);

  // Detect mobile
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768 || 'ontouchstart' in window);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Detect connection quality
  useEffect(() => {
    const update = () => {
      const newQuality = getConnectionQuality();
      setConnectionQuality(prev => {
        if (prev !== newQuality && activeTourIndex !== null) {
          const activeTour = activeTourIndex !== null ? tours?.[activeTourIndex] : null;
          gaTrackMatterportQualitySwitch({
            fromQuality: prev,
            toQuality: newQuality,
            communeName: commune?.name || '',
            tourTitle: activeTour?.title || activeTour?.name || '',
          });
        }
        return newQuality;
      });
    };
    update();
    const conn = navigator?.connection || navigator?.mozConnection || navigator?.webkitConnection;
    conn?.addEventListener('change', update);
    return () => conn?.removeEventListener('change', update);
  }, [activeTourIndex, tours, commune?.name]);

  // Lock body scroll when fullscreen on mobile
  useEffect(() => {
    if (isFullscreen && isMobile) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isFullscreen, isMobile]);

  useEffect(() => {
    if (!commune?.id) return;
    const loadTours = async () => {
      setLoading(true);
      setActiveTourIndex(null);
      try {
        let data = null;
        let error = null;

        const byId = await supabase
          ?.from('communes')
          ?.select('matterport_tours')
          ?.eq('id', commune?.id)
          ?.maybeSingle();

        if (!byId?.error && byId?.data) {
          data = byId?.data;
        } else {
          const byName = await supabase
            ?.from('communes')
            ?.select('matterport_tours')
            ?.ilike('name', commune?.name || '')
            ?.maybeSingle();

          if (!byName?.error && byName?.data) {
            data = byName?.data;
          } else {
            error = byName?.error || byId?.error;
          }
        }

        if (data) {
          const raw = data?.matterport_tours;
          if (Array.isArray(raw) && raw?.length > 0) {
            setTours(raw);
          } else if (typeof raw === 'string') {
            try { setTours(JSON.parse(raw)); } catch { setTours([]); }
          } else {
            setTours([]);
          }
        } else {
          const fallback = commune?.matterport_tours;
          if (Array.isArray(fallback) && fallback?.length > 0) {
            setTours(fallback);
          } else {
            setTours([]);
          }
        }
      } catch {
        setTours([]);
      } finally {
        setLoading(false);
      }
    };
    loadTours();
  }, [commune?.id, commune?.name]);

  // Auto-launch first tour when triggered from hero button
  useEffect(() => {
    if (autoStartTrigger && autoStartTrigger !== prevTriggerRef?.current) {
      prevTriggerRef.current = autoStartTrigger;
      if (tours?.length > 0 && !loading) {
        triggerHaptic('select');
        tourStartedAtRef.current = Date.now();
        gaTrackMatterportTourStart({
          tourTitle: tours?.[0]?.title || tours?.[0]?.name || '',
          communeName: commune?.name || '',
          tourIndex: 0,
          quality: connectionQuality,
        });
        setActiveTourIndex(0);
        setIframeLoading(true);
        pendingAutoStartRef.current = false;
        showGestureHintIfFirst(tours?.length > 1);
      } else {
        pendingAutoStartRef.current = true;
      }
    }
  }, [autoStartTrigger, tours, loading, showGestureHintIfFirst, commune?.name, connectionQuality]);

  // Fire pending auto-start once tours finish loading
  useEffect(() => {
    if (pendingAutoStartRef?.current && tours?.length > 0 && !loading) {
      pendingAutoStartRef.current = false;
      triggerHaptic('select');
      tourStartedAtRef.current = Date.now();
      gaTrackMatterportTourStart({
        tourTitle: tours?.[0]?.title || tours?.[0]?.name || '',
        communeName: commune?.name || '',
        tourIndex: 0,
        quality: connectionQuality,
      });
      setActiveTourIndex(0);
      setIframeLoading(true);
      showGestureHintIfFirst(tours?.length > 1);
    }
  }, [tours, loading, showGestureHintIfFirst, commune?.name, connectionQuality]);

  const activeTour = activeTourIndex !== null ? tours?.[activeTourIndex] : null;
  const embedUrl = activeTour ? getMatterportEmbedUrl(activeTour?.matterport_url, connectionQuality) : null;

  const handlePrev = useCallback(() => {
    triggerHaptic('swipe');
    const fromIndex = activeTourIndex;
    const toIndex = (activeTourIndex - 1 + tours?.length) % tours?.length;
    gaTrackMatterportSwipeGesture({
      direction: 'right',
      communeName: commune?.name || '',
      fromIndex,
      toIndex,
    });
    setActiveTourIndex(toIndex);
    setIframeLoading(true);
  }, [tours?.length, activeTourIndex, commune?.name]);

  const handleNext = useCallback(() => {
    triggerHaptic('swipe');
    const fromIndex = activeTourIndex;
    const toIndex = (activeTourIndex + 1) % tours?.length;
    gaTrackMatterportSwipeGesture({
      direction: 'left',
      communeName: commune?.name || '',
      fromIndex,
      toIndex,
    });
    setActiveTourIndex(toIndex);
    setIframeLoading(true);
  }, [tours?.length, activeTourIndex, commune?.name]);

  const handleSelectTour = (index) => {
    if (activeTourIndex === index) {
      triggerHaptic('close');
      setActiveTourIndex(null);
      setIsFullscreen(false);
      setShowSwipeStrip(false);
    } else {
      triggerHaptic('select');
      setActiveTourIndex(index);
      setIframeLoading(true);
      tourStartedAtRef.current = Date.now();
      gaTrackMatterportTourStart({
        tourTitle: tours?.[index]?.title || tours?.[index]?.name || '',
        communeName: commune?.name || '',
        tourIndex: index,
        quality: connectionQuality,
      });
      showGestureHintIfFirst(tours?.length > 1);
    }
  };

  const handleEnterFullscreen = () => {
    triggerHaptic('fullscreen');
    setIsFullscreen(true);
    fullscreenEnteredAtRef.current = Date.now();
    setShowControls(true);
    resetControlsTimer();
  };

  const handleExitFullscreen = () => {
    triggerHaptic('fullscreen');
    const durationSeconds = fullscreenEnteredAtRef.current
      ? Math.round((Date.now() - fullscreenEnteredAtRef.current) / 1000)
      : 0;
    const activeTour = activeTourIndex !== null ? tours?.[activeTourIndex] : null;
    gaTrackMatterportFullscreenExit({
      communeName: commune?.name || '',
      tourTitle: activeTour?.title || activeTour?.name || '',
      durationSeconds,
    });
    fullscreenEnteredAtRef.current = null;
    setIsFullscreen(false);
    setShowControls(true);
    if (controlsTimerRef?.current) clearTimeout(controlsTimerRef?.current);
  };

  const resetControlsTimer = useCallback(() => {
    if (controlsTimerRef?.current) clearTimeout(controlsTimerRef?.current);
    setShowControls(true);
    if (isFullscreen && isMobile) {
      controlsTimerRef.current = setTimeout(() => setShowControls(false), 3000);
    }
  }, [isFullscreen, isMobile]);

  const handleZoomFeedback = useCallback((direction) => {
    triggerHaptic('zoom');
    const activeTour = activeTourIndex !== null ? tours?.[activeTourIndex] : null;
    gaTrackMatterportPinchZoom({
      direction,
      communeName: commune?.name || '',
      tourTitle: activeTour?.title || activeTour?.name || '',
    });
    setZoomFeedback({ visible: true, direction });
    setTimeout(() => setZoomFeedback({ visible: false, direction }), 600);
  }, [activeTourIndex, tours, commune?.name]);

  const handleIframeLoaded = useCallback(() => {
    triggerHaptic('loaded');
    const loadDurationSeconds = tourStartedAtRef.current
      ? Math.round((Date.now() - tourStartedAtRef.current) / 1000)
      : 0;
    const activeTour = activeTourIndex !== null ? tours?.[activeTourIndex] : null;
    gaTrackMatterportTourLoaded({
      communeName: commune?.name || '',
      tourTitle: activeTour?.title || activeTour?.name || '',
      quality: connectionQuality,
      loadDurationSeconds,
    });
    setIframeLoading(false);
  }, [activeTourIndex, tours, commune?.name, connectionQuality]);

  // Touch gesture ref for the viewer wrapper
  const gestureRef = useTouchGestures({
    enabled: !!activeTour && isMobile,
    onSwipeLeft: handleNext,
    onSwipeRight: handlePrev,
    onPinchZoom: (dir) => handleZoomFeedback(dir),
  });

  const qualityInfo = QUALITY_LABELS?.[connectionQuality] || QUALITY_LABELS?.medium;
  const QualityIcon = qualityInfo?.icon;

  if (loading) {
    return (
      <section className="max-w-7xl mx-auto px-4 lg:px-6 py-12">
        <div className="flex items-center justify-center py-10 gap-3 text-gray-400">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-sm">Chargement des visites immersives...</span>
        </div>
      </section>
    );
  }

  // Empty state
  if (!tours?.length) {
    return (
      <section className="max-w-7xl mx-auto px-4 lg:px-6 py-12">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 rounded-lg bg-brand-gold/10 flex items-center justify-center">
            <Eye className="w-4 h-4 text-brand-gold" />
          </div>
          <span className="text-sm font-semibold text-brand-gold uppercase tracking-wider">
            Visites Immersives 3D
          </span>
        </div>
        <h2 className="text-2xl lg:text-3xl font-heading font-bold text-text-primary mb-6">
          Explorez {commune?.name} en 3D
        </h2>
        <div className="rounded-2xl border border-dashed border-brand-gold/30 bg-brand-gold/5 p-10 flex flex-col items-center justify-center text-center gap-4">
          <div className="w-16 h-16 rounded-full bg-brand-gold/10 flex items-center justify-center">
            <Video className="w-8 h-8 text-brand-gold/60" />
          </div>
          <div>
            <p className="text-text-primary font-semibold text-base mb-1">
              Visites 3D bientôt disponibles
            </p>
            <p className="text-text-secondary text-sm max-w-sm">
              Les visites immersives Matterport pour {commune?.name} seront disponibles prochainement. Revenez bientôt pour explorer cette commune en 3D.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="max-w-7xl mx-auto px-4 lg:px-6 py-12">
      {/* Section Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-brand-gold/10 flex items-center justify-center">
              <Eye className="w-4 h-4 text-brand-gold" />
            </div>
            <span className="text-sm font-semibold text-brand-gold uppercase tracking-wider">
              Visites Immersives 3D
            </span>
          </div>
          <h2 className="text-2xl lg:text-3xl font-heading font-bold text-text-primary">
            Explorez {commune?.name} en 3D
          </h2>
          <p className="text-text-secondary mt-2 text-sm">
            Visitez les infrastructures et services de {commune?.name} depuis chez vous grâce aux visites virtuelles Matterport.
          </p>
        </div>
        <div className="hidden md:flex items-center gap-1.5 bg-brand-gold/10 border border-brand-gold/20 rounded-full px-4 py-1.5">
          <MapPin className="w-3.5 h-3.5 text-brand-gold" />
          <span className="text-xs font-semibold text-brand-gold">{tours?.length} visite{tours?.length > 1 ? 's' : ''}</span>
        </div>
      </div>

      {/* Active Tour Viewer */}
      {activeTour && embedUrl && (
        <div
          className={`mb-8 rounded-2xl overflow-hidden border border-brand-gold/20 shadow-warm bg-black ${
            isFullscreen
              ? 'fixed inset-0 z-50 rounded-none border-0 m-0 flex flex-col' : ''
          }`}
          onClick={isFullscreen && isMobile ? resetControlsTimer : undefined}
        >
          {/* Viewer Header — auto-hides on mobile fullscreen */}
          <div
            className={`flex items-center justify-between px-4 py-3 bg-gray-900/95 backdrop-blur-sm transition-all duration-300 ${
              isFullscreen && isMobile && !showControls
                ? 'opacity-0 pointer-events-none -translate-y-full' : 'opacity-100 translate-y-0'
            }`}
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className={`flex-shrink-0 text-xs px-2.5 py-1 rounded-full font-semibold ${CATEGORY_COLORS?.[activeTour?.category] || 'bg-gray-100 text-gray-600'}`}>
                {CATEGORY_LABELS?.[activeTour?.category] || activeTour?.category}
              </div>
              <div className="min-w-0">
                <p className="text-white font-semibold text-sm truncate">{activeTour?.title}</p>
                {activeTour?.description && (
                  <p className="text-gray-400 text-xs truncate">{activeTour?.description}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0 ml-3">
              {/* Connection quality badge */}
              <div className={`flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-md bg-black/40 ${qualityInfo?.color}`}>
                <QualityIcon className="w-3 h-3" />
                <span>{qualityInfo?.label}</span>
              </div>
              {tours?.length > 1 && (
                <>
                  <button
                    onClick={handlePrev}
                    className="p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors touch-manipulation"
                    title="Visite précédente"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-gray-500 text-xs font-mono">
                    {activeTourIndex + 1}/{tours?.length}
                  </span>
                  <button
                    onClick={handleNext}
                    className="p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors touch-manipulation"
                    title="Visite suivante"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </>
              )}
              <button
                onClick={isFullscreen ? handleExitFullscreen : handleEnterFullscreen}
                className="p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors touch-manipulation"
                title={isFullscreen ? 'Quitter le plein écran' : 'Plein écran'}
              >
                {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </button>
              <button
                onClick={() => {
                  triggerHaptic('close');
                  setActiveTourIndex(null);
                  setIsFullscreen(false);
                  setShowSwipeStrip(false);
                }}
                className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors touch-manipulation"
                title="Fermer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Iframe wrapper with gesture layer */}
          <div
            ref={gestureRef}
            className={`relative flex-1 ${isFullscreen ? 'h-full' : ''}`}
            style={isFullscreen ? {} : { paddingTop: '56.25%' }}
          >
            {/* Enhanced loading overlay with progress bar */}
            <IframeLoadingOverlay
              visible={iframeLoading}
              quality={connectionQuality}
              tourTitle={activeTour?.title}
            />

            {/* Gesture hint overlay — shown on first tour launch, persistent until dismissed */}
            <GestureHint
              visible={showGestureHint}
              onDismiss={() => setShowGestureHint(false)}
              hasTours={tours?.length > 1}
            />

            {/* Zoom feedback */}
            <ZoomIndicator visible={zoomFeedback?.visible} direction={zoomFeedback?.direction} />

            <iframe
              key={`${embedUrl}-${connectionQuality}`}
              src={embedUrl}
              className="w-full h-full absolute inset-0"
              allowFullScreen
              allow="xr-spatial-tracking; gyroscope; accelerometer; fullscreen"
              title={activeTour?.title}
              onLoad={handleIframeLoaded}
              style={{ border: 'none' }}
            />

            {/* Mobile fullscreen bottom navigation bar */}
            {isFullscreen && isMobile && tours?.length > 1 && (
              <div
                className={`absolute bottom-0 left-0 right-0 z-20 transition-all duration-300 ${
                  showControls ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-full pointer-events-none'
                }`}
              >
                <div className="bg-gray-900/90 backdrop-blur-md border-t border-white/10 px-4 py-3 flex items-center justify-between gap-3">
                  <button
                    onClick={handlePrev}
                    className="flex items-center gap-2 px-4 py-2.5 bg-white/10 hover:bg-white/20 active:bg-white/30 rounded-xl text-white transition-colors touch-manipulation min-w-[80px] justify-center"
                  >
                    <ChevronLeft className="w-5 h-5" />
                    <span className="text-sm font-medium">Préc.</span>
                  </button>

                  <div className="flex items-center gap-1.5 flex-1 justify-center overflow-hidden">
                    {tours?.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => {
                          triggerHaptic('select');
                          setActiveTourIndex(i);
                          setIframeLoading(true);
                        }}
                        className={`rounded-full transition-all touch-manipulation ${
                          i === activeTourIndex
                            ? 'w-5 h-2 bg-brand-gold' : 'w-2 h-2 bg-white/30 hover:bg-white/60'
                        }`}
                      />
                    ))}
                  </div>

                  <button
                    onClick={handleNext}
                    className="flex items-center gap-2 px-4 py-2.5 bg-white/10 hover:bg-white/20 active:bg-white/30 rounded-xl text-white transition-colors touch-manipulation min-w-[80px] justify-center"
                  >
                    <span className="text-sm font-medium">Suiv.</span>
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}

            {/* Tap-to-show-controls hint (mobile fullscreen) */}
            {isFullscreen && isMobile && !showControls && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 pointer-events-none">
                <div className="bg-black/50 text-white/60 text-xs px-3 py-1.5 rounded-full backdrop-blur-sm">
                  Touchez pour afficher les contrôles
                </div>
              </div>
            )}
          </div>

          {/* Persistent swipe hint strip (non-fullscreen, dismissible) */}
          {!isFullscreen && isMobile && tours?.length > 1 && (
            <SwipeHintStrip
              visible={showSwipeStrip}
              onDismiss={() => setShowSwipeStrip(false)}
            />
          )}
        </div>
      )}

      {/* Tour Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {tours?.map((tour, index) => {
          const isActive = activeTourIndex === index;
          const categoryColor = CATEGORY_COLORS?.[tour?.category] || 'bg-gray-100 text-gray-600';
          return (
            <button
              key={tour?.id || index}
              onClick={() => handleSelectTour(index)}
              className={`group text-left rounded-xl overflow-hidden border-2 transition-all duration-300 touch-manipulation ${
                isActive
                  ? 'border-brand-gold shadow-warm scale-[1.02]'
                  : 'border-transparent hover:border-brand-gold/40 hover:shadow-md bg-white'
              }`}
            >
              {/* Thumbnail */}
              <div className="relative h-40 bg-gray-100 overflow-hidden">
                {tour?.thumbnail_url ? (
                  <img
                    src={tour?.thumbnail_url}
                    alt={tour?.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
                    <div className="w-12 h-12 rounded-full bg-brand-gold/20 flex items-center justify-center mb-2">
                      <Eye className="w-6 h-6 text-brand-gold" />
                    </div>
                    <span className="text-gray-400 text-xs font-medium">Visite 3D</span>
                  </div>
                )}

                {/* Play overlay */}
                <div className={`absolute inset-0 flex items-center justify-center transition-all duration-300 ${
                  isActive ? 'bg-brand-gold/20' : 'bg-black/0 group-hover:bg-black/30'
                }`}>
                  {!isActive && (
                    <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 shadow-lg">
                      <Play className="w-5 h-5 text-gray-900 ml-0.5" fill="currentColor" />
                    </div>
                  )}
                  {isActive && (
                    <div className="w-12 h-12 rounded-full bg-brand-gold flex items-center justify-center shadow-lg">
                      <Eye className="w-5 h-5 text-white" />
                    </div>
                  )}
                </div>

                {/* 3D Badge */}
                <div className="absolute top-2 right-2 bg-black/70 backdrop-blur-sm text-white text-xs font-bold px-2 py-0.5 rounded-md">
                  3D
                </div>

                {/* Active indicator */}
                {isActive && (
                  <div className="absolute top-2 left-2 bg-brand-gold text-white text-xs font-bold px-2 py-0.5 rounded-md">
                    En cours
                  </div>
                )}
              </div>

              {/* Card Content */}
              <div className={`p-3 ${isActive ? 'bg-brand-gold/5' : 'bg-white'}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className={`font-semibold text-sm truncate ${isActive ? 'text-brand-gold' : 'text-text-primary'}`}>
                      {tour?.title}
                    </p>
                    {tour?.description && (
                      <p className="text-xs text-text-secondary mt-0.5 line-clamp-1">{tour?.description}</p>
                    )}
                  </div>
                  {tour?.category && (
                    <span className={`flex-shrink-0 text-xs px-2 py-0.5 rounded-full font-medium ${categoryColor}`}>
                      {CATEGORY_LABELS?.[tour?.category] || tour?.category}
                    </span>
                  )}
                </div>
                <div className={`mt-2 flex items-center gap-1.5 text-xs font-medium ${isActive ? 'text-brand-gold' : 'text-gray-400 group-hover:text-brand-gold'} transition-colors`}>
                  <Play className="w-3 h-3" fill="currentColor" />
                  {isActive ? 'Visite en cours' : 'Lancer la visite'}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
};

export default ImmersiveTours;
